import { MediaElement, MediaImage, Rect } from 'editor/src/store/design/types';

import getCenteredImagePosition from 'editor/src/util/2d/getCenteredImagePosition';
import limitPrecision from 'editor/src/util/limitPrecision';

export interface SlotTransform {
  from: Rect;
  to: Rect;
  sx: number;
  sy: number;
}

export function getSlotTransform(from: Rect, to: Rect): SlotTransform {
  return { from, to, sx: to.width / from.width, sy: to.height / from.height };
}

const mapX = (t: SlotTransform, x: number) => limitPrecision(t.to.x + (x - t.from.x) * t.sx);
const mapY = (t: SlotTransform, y: number) => limitPrecision(t.to.y + (y - t.from.y) * t.sy);

/**
 * The geometry a layer takes when it moves from one frame to another: its place relative to
 * the frame is kept, and it scales with the frame. Frames of one format share a size, so the
 * scale is 1 unless the author changes the grid.
 */
export function transformSlotGeometry(element: MediaElement, t: SlotTransform): Partial<MediaElement> {
  if (element.type === 'line') {
    return {
      x1: mapX(t, element.x1),
      y1: mapY(t, element.y1),
      x2: mapX(t, element.x2),
      y2: mapY(t, element.y2),
    } as Partial<MediaElement>;
  }

  const update: Record<string, unknown> = {
    x: mapX(t, element.x),
    y: mapY(t, element.y),
    width: limitPrecision(element.width * t.sx),
    height: limitPrecision(element.height * t.sy),
  };
  const uniform = Math.abs(t.sx - t.sy) < 0.01;
  if (element.type === 'image' && !uniform) {
    // A frame that changes proportions gets the picture re-fitted, not stretched with it.
    Object.assign(
      update,
      getCenteredImagePosition({ ...element, ...update } as MediaImage, { width: element.pw, height: element.ph }),
    );
  } else if (element.type === 'image' || element.type === 'addon') {
    update.px = limitPrecision(element.px * t.sx);
    update.py = limitPrecision(element.py * t.sy);
    update.pw = limitPrecision(element.pw * t.sx);
    update.ph = limitPrecision(element.ph * t.sy);
  }
  // The width drives the font: text wraps at the box width, and scaling by one axis means
  // the frame going back restores the size.
  if (element.type === 'text' && t.sx !== 1) {
    // A scaled copy no longer has its style's size, so it stops following the style
    // (GAPI-61895): a style edit would otherwise reset it to the unscaled size.
    update.extra = {
      ...element.extra,
      fontSize: limitPrecision(element.extra.fontSize * t.sx),
      textStyleId: undefined,
    };
  }
  return update as Partial<MediaElement>;
}
